import React, { FC, useCallback, useMemo, useState } from 'react';
import { useEffectOnce, Dialog, Button } from '@beeline.com/bp-design-system';
import { useTypedMutation } from 'src/utils/hooks/useTypedMutation';
import { CustomFieldDefinitionDataTypeModel, CustomFieldDefinitionModelInput } from 'src/graphql/types';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { AddCustomFieldDefinitionDocument } from '../utils/__generated__/AddCustomFieldDefinition.graphql';
import { checkIfFormIsValid } from '../utils/CustomFieldDefinitions.utils';
import { UpdateCustomFieldDefinitionDocument } from '../utils/__generated__/UpdateCustomFieldDefinition.graphql';
import { SaveCustomFieldForm } from './SaveCustomFieldForm';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { GetAllCustomFieldValuesDocument } from '../utils/__generated__/GetCustomFieldValues.graphql';
export interface Props {
  customField: CustomFieldDefinitionModelInput | undefined;
  onClose: () => void;
  mode: 'edit' | 'add';
  isObject?: boolean;
  refetch: () => void;
}
export const SaveCustomFieldDefinitionDialog: FC<React.PropsWithChildren<Props>> = ({
  isObject,
  customField,
  onClose,
  mode,
  refetch
}) => {
  const intl = useCustomFieldsAndObjectsIntl();
  const isEdit = mode === 'edit';
  const { get: getCustomFieldValues } = useTypedQuery(GetAllCustomFieldValuesDocument, {
    variables: { uid: customField?.uid ?? '' },
    onCompleted(data) {
      const values = data?.customFieldDefinition?.values ?? [];
      setCustomFieldDefinition({
        ...(customField || {}),
        values
      });
    }
  });
  useEffectOnce(() => {
    // For edit mode, we need to fetch the custom field values
    if (isEdit) {
      getCustomFieldValues();
    } else {
      // For add mode, we need to set the default custom field definition
      setCustomFieldDefinition({
        dataType: CustomFieldDefinitionDataTypeModel.String,
        ...(customField || {})
      });
    }
  });
  const [customFieldDefinition, setCustomFieldDefinition] = useState<CustomFieldDefinitionModelInput | undefined>();
  const { set: addCustomFieldDefinition, loading: addLoading } = useTypedMutation(AddCustomFieldDefinitionDocument);
  const { set: updateCustomFieldDefinition, loading: updateLoading } = useTypedMutation(
    UpdateCustomFieldDefinitionDocument
  );
  const onCloseHandler = useCallback(() => {
    onClose();
    setCustomFieldDefinition(undefined);
  }, [onClose]);
  const getSantiseCustomField = useCallback(() => {
    if (customFieldDefinition) {
      if (customFieldDefinition.dataType === 'COST_CENTER') {
        return {
          ...customFieldDefinition,
          values: customFieldDefinition.values?.filter((v) => v?.referenceId?.trim().length ?? false)
        };
      }
      return {
        ...customFieldDefinition,
        values: customFieldDefinition.values
          ?.filter((v) => v?.value?.trim().length ?? false)
          .map((v) => ({ ...v, label: v?.label?.trim(), value: v?.value?.trim() }))
      };
    }
    return;
  }, [customFieldDefinition]);
  const onSubmit = useCallback(async () => {
    let response;
    const sanitisedCustomField = getSantiseCustomField();
    if (!isEdit && !!sanitisedCustomField) {
      response = await addCustomFieldDefinition({
        variables: { customFieldDefinition: sanitisedCustomField }
      });
    }
    if (isEdit && !!sanitisedCustomField) {
      response = await updateCustomFieldDefinition({
        variables: { customFieldDefinition: sanitisedCustomField }
      });
    }
    if (!response?.errors) {
      onCloseHandler();
      refetch();
    }
  }, [addCustomFieldDefinition, getSantiseCustomField, isEdit, onCloseHandler, refetch, updateCustomFieldDefinition]);
  const onCustomFieldChange = useCallback((newCustomFieldDef: CustomFieldDefinitionModelInput) => {
    setCustomFieldDefinition((prevState) => ({ ...prevState, ...newCustomFieldDef }));
  }, []);
  const title = useMemo(() => {
    if (isObject) {
      return isEdit ? intl.editObject : intl.addObject;
    }
    return isEdit ? intl.editCustomFieldDefinition : intl.addCustomFieldDefinition;
  }, [intl, isEdit, isObject]);
  return (
    <Dialog
      title={title}
      onClose={onCloseHandler}
      open
      show={{ cancelButton: true }}
      disableBackdropClick
      actions={[
        <Button
          variant="contained"
          color="primary"
          key="confirm_button"
          disabled={!checkIfFormIsValid(customFieldDefinition, true)}
          onClick={onSubmit}
          loading={addLoading || updateLoading}
          dataTest="confirm_button"
        >
          {isEdit ? intl.save : intl.add}
        </Button>
      ]}
    >
      <SaveCustomFieldForm
        isChildOfObject={isObject}
        isEditing={isEdit}
        onCustomFieldChange={onCustomFieldChange}
        customFieldDefinition={customFieldDefinition}
      />
    </Dialog>
  );
};
